import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountCircle as AccountCircleIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../services/apiClient";
import { User, UserRole } from "../types/api-types";
import LoadingSpinner from "../components/Common/LoadingSpinner";

interface ProfileFormState {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface PasswordFormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const emptyProfileForm: ProfileFormState = {
  username: "",
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
};

const emptyPasswordForm: PasswordFormState = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const roleLabels: Record<UserRole, string> = {
  [UserRole.ADMIN]: "Administrator",
  [UserRole.MANAGER]: "Manager",
  [UserRole.CASHIER]: "Kassierer",
  [UserRole.VIEWER]: "Betrachter",
};

const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, refreshUser, updateCurrentUser } = useAuth();
  const [profileForm, setProfileForm] =
    useState<ProfileFormState>(emptyProfileForm);
  const [passwordForm, setPasswordForm] =
    useState<PasswordFormState>(emptyPasswordForm);
  const [loading, setLoading] = useState(!user);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const fillProfileForm = useCallback((currentUser: User) => {
    setProfileForm({
      username: currentUser.username || "",
      firstName: currentUser.firstName || "",
      lastName: currentUser.lastName || "",
      email: currentUser.email || "",
      phoneNumber: currentUser.phoneNumber || "",
    });
  }, []);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        setLoading(true);
        const currentUser = await refreshUser();
        fillProfileForm(currentUser);
      } catch (error) {
        setProfileMessage({
          type: "error",
          text: "Profil konnte nicht geladen werden.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fillProfileForm(user);
      setLoading(false);
    } else {
      loadCurrentUser();
    }
  }, [user, refreshUser, fillProfileForm]);

  const setProfileField = (field: keyof ProfileFormState, value: string) => {
    setProfileForm((current) => ({ ...current, [field]: value }));
  };

  const setPasswordField = (field: keyof PasswordFormState, value: string) => {
    setPasswordForm((current) => ({ ...current, [field]: value }));
  };

  const validateProfile = (): string | null => {
    if (profileForm.username.trim().length < 2) {
      return "Der Benutzername muss mindestens 2 Zeichen lang sein.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileForm.email.trim())) {
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }

    return null;
  };

  const validatePassword = (): string | null => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      return "Aktuelles und neues Passwort sind erforderlich.";
    }

    if (passwordForm.newPassword.length < 8) {
      return "Das neue Passwort muss mindestens 8 Zeichen lang sein.";
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return "Die Passwortbestätigung stimmt nicht überein.";
    }

    return null;
  };

  const handleProfileSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileMessage(null);

    const validationError = validateProfile();
    if (validationError) {
      setProfileMessage({ type: "error", text: validationError });
      return;
    }

    try {
      setProfileSaving(true);
      const updatedUser = await apiClient.updateCurrentUser({
        username: profileForm.username.trim(),
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        email: profileForm.email.trim(),
        phoneNumber: profileForm.phoneNumber.trim(),
      });
      updateCurrentUser(updatedUser);
      fillProfileForm(updatedUser);
      setProfileMessage({
        type: "success",
        text: "Profil wurde gespeichert.",
      });
    } catch (error: any) {
      setProfileMessage({
        type: "error",
        text: error.message || "Profil konnte nicht gespeichert werden.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordMessage(null);

    const validationError = validatePassword();
    if (validationError) {
      setPasswordMessage({ type: "error", text: validationError });
      return;
    }

    try {
      setPasswordSaving(true);
      await apiClient.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm(emptyPasswordForm);
      setPasswordMessage({
        type: "success",
        text: "Passwort wurde geändert.",
      });
    } catch (error: any) {
      setPasswordMessage({
        type: "error",
        text: error.message || "Passwort konnte nicht geändert werden.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const getRoleColor = (role?: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return "error";
      case UserRole.MANAGER:
        return "warning";
      case UserRole.CASHIER:
        return "primary";
      default:
        return "default";
    }
  };

  if (loading) {
    return <LoadingSpinner text="Lade Profil..." />;
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 1, md: 3 },
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 700,
                mb: 1,
              }}
            >
              <AccountCircleIcon fontSize={isMobile ? "medium" : "large"} />
              Profil
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Persönliche Daten und Passwort verwalten
            </Typography>
          </Box>

          <Chip
            label={user?.role ? roleLabels[user.role] : "Rolle"}
            color={getRoleColor(user?.role) as any}
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              color: "white",
              borderColor: "rgba(255,255,255,0.5)",
              fontWeight: 700,
            }}
            variant="outlined"
          />
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Profildaten
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rolle und Aktivstatus werden von berechtigten Personen
                    verwaltet.
                  </Typography>
                </Box>

                {profileMessage && (
                  <Alert
                    severity={profileMessage.type}
                    onClose={() => setProfileMessage(null)}
                  >
                    {profileMessage.text}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vorname"
                      value={profileForm.firstName}
                      onChange={(event) =>
                        setProfileField("firstName", event.target.value)
                      }
                      autoComplete="given-name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nachname"
                      value={profileForm.lastName}
                      onChange={(event) =>
                        setProfileField("lastName", event.target.value)
                      }
                      autoComplete="family-name"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Benutzername"
                      value={profileForm.username}
                      onChange={(event) =>
                        setProfileField("username", event.target.value)
                      }
                      autoComplete="username"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="E-Mail"
                      type="email"
                      value={profileForm.email}
                      onChange={(event) =>
                        setProfileField("email", event.target.value)
                      }
                      autoComplete="email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={profileForm.phoneNumber}
                      onChange={(event) =>
                        setProfileField("phoneNumber", event.target.value)
                      }
                      autoComplete="tel"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={profileSaving}
                    fullWidth={isMobile}
                  >
                    {profileSaving ? "Speichert..." : "Profil speichern"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Passwort
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Das aktuelle Passwort ist für Änderungen erforderlich.
                  </Typography>
                </Box>

                {passwordMessage && (
                  <Alert
                    severity={passwordMessage.type}
                    onClose={() => setPasswordMessage(null)}
                  >
                    {passwordMessage.text}
                  </Alert>
                )}

                <Divider />

                <TextField
                  fullWidth
                  required
                  label="Aktuelles Passwort"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(event) =>
                    setPasswordField("currentPassword", event.target.value)
                  }
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Neues Passwort"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(event) =>
                    setPasswordField("newPassword", event.target.value)
                  }
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  required
                  label="Neues Passwort bestätigen"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) =>
                    setPasswordField("confirmPassword", event.target.value)
                  }
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={<LockIcon />}
                    disabled={passwordSaving}
                    fullWidth={isMobile}
                  >
                    {passwordSaving ? "Ändert..." : "Passwort ändern"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;
